import type {Styles} from '@core/store/SettingsStore';
import {createDefaultStyles} from '@core/store/SettingsStore';

/** Pre-2026 style.json palette: Vuetify `toolbar` + `cardHeader` instead of secondary/accent. */
export interface LegacyStyleDetail {
  primary?: string;
  toolbar?: string;
  cardHeader?: string;
  secondary?: string;
  accent?: string;
  background?: string;
}

export type LegacyStylesPayload = Omit<Styles, 'dark' | 'light'> & {
  dark?: LegacyStyleDetail;
  light?: LegacyStyleDetail;
};

function isLegacyStyleDetail(detail: LegacyStyleDetail | undefined): boolean {
  if (!detail || typeof detail !== 'object') {
    return false;
  }
  const hasLegacyKeys =
    typeof detail.toolbar === 'string' || typeof detail.cardHeader === 'string';
  const missingNewKeys =
    typeof detail.secondary !== 'string' || typeof detail.accent !== 'string';
  return hasLegacyKeys && missingNewKeys;
}

/**
 * Maps legacy style.json fields to the current brand palette:
 * - `toolbar` → `secondary` (old Vuetify toolbar / app bar color)
 * - `cardHeader` → `accent` (old Vuetify card header highlight)
 * - `background` falls back to {@link createDefaultStyles} when absent
 */
export function migrateLegacyStyleDetail(
  detail: LegacyStyleDetail | undefined,
  mode: 'light' | 'dark',
): Styles['light'] {
  const defaults = createDefaultStyles()[mode];
  if (!detail) {
    return {...defaults};
  }

  const primary = detail.primary ?? defaults.primary;
  const secondary =
    detail.secondary ?? detail.toolbar ?? primary ?? defaults.secondary;
  const accent =
    detail.accent ??
    detail.cardHeader ??
    detail.toolbar ??
    primary ??
    defaults.accent;
  const background = detail.background ?? defaults.background;

  return {primary, secondary, accent, background};
}

function toStyleDetail(
  detail: LegacyStyleDetail | undefined,
  mode: 'light' | 'dark',
): Styles['light'] {
  if (isLegacyStyleDetail(detail)) {
    return migrateLegacyStyleDetail(detail, mode);
  }
  const defaults = createDefaultStyles()[mode];
  if (!detail) {
    return {...defaults};
  }
  return {
    primary: detail.primary ?? defaults.primary,
    secondary: detail.secondary ?? defaults.secondary,
    accent: detail.accent ?? defaults.accent,
    background: detail.background ?? defaults.background,
  };
}

/** Normalizes fetched style.json, including legacy toolbar/cardHeader payloads. */
export function migrateLegacyStyleJson(data: LegacyStylesPayload): Styles {
  const defaults = createDefaultStyles();
  return {
    ...defaults,
    ...data,
    light: toStyleDetail(data.light, 'light'),
    dark: toStyleDetail(data.dark, 'dark'),
  };
}
